import { AgBytesAPI } from "@ai360/core";

import { computeIsSelectable } from "~/utils/products/use-product-list";
import { messages as globalMessages } from "~/i18n-messages";
import {
    FormatCropTooltipMessage,
    CropScope,
    getCropListRequest,
    getRefetchOnMountOrArgChange,
    ILinkageStampable,
} from "~/utils/crops/use-crop-list";

const EMPTY_CROP_PURPOSES: AgBytesAPI.ICropPurposeSelectionListItem[] = [];

export interface IUseCropPurposeListOptions {
    cropGuid?: string | null;
    isEnabled?: boolean;
    scope: CropScope;
}

export interface IUseCropPurposeListResult {
    cropPurposes: AgBytesAPI.ICropPurposeSelectionListItem[];
    isFetching: boolean;
}

/** `getCropListRequest` plus `cropGuid`, so hooks, selectors and sagas share one cache key. */
export const getCropPurposeListRequest = ({
    cropGuid,
    scope,
}: {
    cropGuid?: string | null;
    scope: CropScope;
}): AgBytesAPI.ICropPurposeSelectionListRequest => ({
    ...getCropListRequest(scope),
    ...(cropGuid && { cropGuid }),
});

/**
 * One RTK cache entry over `POST agbytes/v1.0/crop-purpose`. Unlike `useCropList()`, the cache
 * key includes `cropGuid` -- Purpose is a dependent field, so a crop change must re-fetch. With
 * no `cropGuid` (nothing selected yet), still call the endpoint -- it degrades to
 * `isAgvanceLinked: null` on every row and returns the full unrestricted list, same as today.
 * Never skip the call just because a crop hasn't been picked.
 */
export const useCropPurposeList = ({
    cropGuid = null,
    isEnabled = true,
    scope,
}: IUseCropPurposeListOptions): IUseCropPurposeListResult => {
    const { data: cropPurposes = EMPTY_CROP_PURPOSES, isFetching } =
        AgBytesAPI.useGetCropPurposeSelectionListQuery(
            getCropPurposeListRequest({ cropGuid, scope }),
            { skip: !isEnabled, refetchOnMountOrArgChange: getRefetchOnMountOrArgChange(scope) }
        );
    return { cropPurposes, isFetching };
};

export interface ICropPurposeDropdownOption {
    activeYn: boolean;
    guid: string;
    id: string;
    isAgvanceLinked: boolean | null;
    name: string;
}

/**
 * Narrows a `useCropPurposeList()` result to the plain option shape crop-purpose consumers
 * expect, the same role `toCropDropdownOptions()` plays for crops.
 */
export const toCropPurposeDropdownOptions = (
    cropPurposes: AgBytesAPI.ICropPurposeSelectionListItem[]
): ICropPurposeDropdownOption[] =>
    cropPurposes.map((cropPurpose) => ({
        activeYn: cropPurpose.activeYn,
        guid: cropPurpose.cropPurposeGuid,
        id: cropPurpose.cropPurposeId,
        isAgvanceLinked: cropPurpose.isAgvanceLinked,
        name: cropPurpose.name,
    }));

/**
 * Stamps `isSelectable` (and, given `formatMessage`, an unlinked-purpose `getToolTip`) onto any
 * option list carrying `activeYn`/`isAgvanceLinked` -- reuses `computeIsSelectable`, the same
 * mechanism Linked Crop Only already shipped for crops (Decision 6), not a new one. `showInactive`
 * (default false, mirrors `computeIsSelectable`'s own default) lets a caller whose pre-existing
 * Purpose picker never hid inactive rows (e.g. `analysis-management-area-form.tsx`, which had no
 * `activeYn` data at all before this migration) preserve that -- inactive rows stay selectable,
 * only an explicit `isAgvanceLinked === false` hides.
 */
export const stampCropPurposeSelectable = <T extends ILinkageStampable>(
    options: T[],
    formatMessage?: FormatCropTooltipMessage,
    showInactive: boolean = false
): (T & { isSelectable: boolean; getToolTip?: () => string })[] =>
    options.map((option) => ({
        ...option,
        isSelectable: computeIsSelectable(option.activeYn, option.isAgvanceLinked, showInactive),
        ...(formatMessage &&
            option.isAgvanceLinked === false && {
                getToolTip: () => formatMessage(globalMessages.cropPurposeNotLinkedInAgvance),
            }),
    }));

export interface ICropPurposeSelectOption {
    activeYn: boolean;
    isAgvanceLinked: boolean | null;
    isSelectable: boolean;
    getToolTip?: () => string;
    label: string;
    value: string;
}

/**
 * The `{value, label}`-keyed counterpart to `toCropPurposeDropdownOptions()`, for
 * `SelectInput`-direct call sites -- the shape every migrated Events form actually renders
 * (mirrors `toCropSelectOptions()`). No sort here -- `agbytes/v1.0/crop-purpose` already
 * natural-sorts by name server-side (`CropPurposeService.SelectionList`), so this stays a
 * straight passthrough of that order.
 */
export const toCropPurposeSelectOptions = (
    cropPurposes: ICropPurposeDropdownOption[],
    formatMessage: FormatCropTooltipMessage
): ICropPurposeSelectOption[] =>
    stampCropPurposeSelectable(
        cropPurposes.map((cropPurpose) => ({
            activeYn: cropPurpose.activeYn,
            isAgvanceLinked: cropPurpose.isAgvanceLinked,
            label: cropPurpose.name,
            value: cropPurpose.guid,
        })),
        formatMessage
    );

/**
 * Mirrors `use-crop-list.ts`'s `isCropUnlinked` -- a plain function, not a hook, so it can be
 * called from `mapStateToProps`, sagas, or a class `render()`. Returns `false` when the guid
 * isn't found at all (covers the in-flight window right after `cropGuid` changes, when the new
 * purpose list hasn't arrived yet) and only `true` on an explicit `isAgvanceLinked === false` --
 * never treat "not found yet" as "blocked."
 */
export const isCropPurposeUnlinked = (
    cropPurposeGuid: string | null | undefined,
    cropPurposes: ICropPurposeDropdownOption[]
): boolean => {
    if (!cropPurposeGuid) {
        return false;
    }
    const cropPurpose = cropPurposes.find((c) => c.guid === cropPurposeGuid);
    return cropPurpose != null && cropPurpose.isAgvanceLinked === false;
};
